import React, { useState } from 'react';
import { PropTypes } from 'prop-types';
import Table from '../../componentsV2/table/Table';
import Pagination from '../../componentsV2/pagination/Pagination';
import Button from '../../componentsV2/Button/Button';
import { Plus } from '../../componentsV2/Icons/Icons';
import NoSearchResults from '../../componentsV2/NoSearchResults/NoSearchResults';
import EmptyState from '../../componentsV2/EmptyState/EmptyState';
import CommonModal from '../../componentsV2/Common/Modal/Modal';
import NewEditContactModal from '../../componentsV2/Contacts/NewEditContactModal';
import { useUnsavedChangesWarning, useCloseDialogWithUnsavedChanges } from '../../hooks';
import { useUrlModals } from '../../utils/useUrlModals';
import PagesHeader from '../../componentsV2/Headers/PagesHeader';
import ResultCount from '../../componentsV2/ResultCount/ResultCount';
import TableSkeletonLoader from '../../componentsV2/LoadingSkeletons/TableSkeletonLoader';

const DEFAULT_INITIAL_MODEL_FORM_ITEMS = [];

const ContactsPage = ({
  organizationId,
  initialModelFormItems = DEFAULT_INITIAL_MODEL_FORM_ITEMS,
  currentPage,
  totalContacts = null,
  totalUnfilteredContacts = null,
  contacts,
  setIsNewContact,
  itemsPerPage,
  onPageChange,
  showToast,
  onSort,
  isLoading = false,
  isSearchActive = false,
  params = {},
}) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const currentOffset = (currentPage - 1) * itemsPerPage;
  const { modals, openModal, closeModal } = useUrlModals({
    createContact: { isIdParam: false },
  });

  useUnsavedChangesWarning(hasUnsavedChanges);

  const handleCloseDialogWithUnsavedChanges = useCloseDialogWithUnsavedChanges({
    isUnsavedChanges: hasUnsavedChanges,
    setIsUnsavedChanges: setHasUnsavedChanges,
    redirectCallback: () => closeModal('createContact'),
  });

  const columns = [
    {
      title: 'Name', dataIndex: 'name', className: 'w-40', isSortable: true,
    },
    {
      title: 'Contact type', dataIndex: 'contact_type', className: 'w-52', isSortable: true,
    },
    {
      title: 'Company', dataIndex: 'company', className: 'w-40', isSortable: true,
    },
    {
      title: 'Related sites', dataIndex: 'sites_count', className: 'w-48 text-center', isSortable: true,
    },
    {
      title: 'Priority', dataIndex: 'highest_priority', className: 'w-40', isSortable: true,
    },
    {
      title: 'H2a', dataIndex: 'any_sites_h2a', className: 'w-24', isSortable: true,
    },
  ];

  const emptyStateContent = (
    <EmptyState pageType="contacts" onAdd={() => openModal('createContact')} dataTestId="new-contact-button" />
  );

  let renderContent;
  if (isLoading) {
    renderContent = (
      <div className="pt-20 w-full">
        <TableSkeletonLoader
          numRows={itemsPerPage}
          numColumns={6}
          skeletonHeight={16}
          skeletonWidth="100%"
        />
      </div>
    );
  } else if (contacts.length > 0) {
    renderContent = (
      <>
        <div className="flex w-full gap-4 justify-between items-center pb-4">
          <PagesHeader name="contact" label="Contacts" size="h-9" />
          {totalContacts > 0 && (
          <div className="flex-1 flex justify-center">
            <ResultCount
              offset={currentOffset}
              pageSize={contacts.length}
              total={totalContacts}
              unfilteredTotal={totalUnfilteredContacts}
              viewType="table"
              pageType="contacts"
            />
          </div>
          )}
          <Button
            label="New Contact"
            Icon={Plus}
            onClick={() => openModal('createContact')}
            aria-label="New Contact"
            dataTestId="new-contact-button"
          />
        </div>
        <Table
          dataSource={contacts}
          columns={columns}
          onSort={onSort}
          typeOfData="contacts"
          defaultSortOrder={{ sortBy: params.sortBy || 'name', order: params.direction || 'asc' }}
        />
        <Pagination
          itemsPerPage={itemsPerPage}
          totalItems={totalContacts}
          currentPage={currentPage}
          setCurrentPage={onPageChange}
        />
      </>
    );
  } else if (totalUnfilteredContacts === 0) {
    renderContent = emptyStateContent;
  } else if (isSearchActive) {
    renderContent = <NoSearchResults pageType="contacts" />;
  } else {
    renderContent = emptyStateContent;
  }

  return (
    <>
      {renderContent}

      {modals.createContact?.isOpen && (
        <CommonModal
          title="Create Contact"
          titlePosition="left"
          className="py-0"
          headerClassName="h-18.5 pl-5.5"
          closeIconPosition="absolute right-5.5 top-6"
          titleStyle="text-2xl font-bold"
          boxClassName="w-1/2"
          handleClose={handleCloseDialogWithUnsavedChanges}
          isHeaderDivider
        >
          <NewEditContactModal
            organizationId={organizationId}
            initialModelFormItems={initialModelFormItems}
            onCancel={handleCloseDialogWithUnsavedChanges}
            setIsModalVisible={(visible) => (visible ? openModal('createContact') : closeModal('createContact'))}
            showToast={showToast}
            setIsNewContact={setIsNewContact}
            setIsUnsavedChanges={setHasUnsavedChanges}
            closeModal={() => closeModal('createContact')}
          />
        </CommonModal>
      )}
    </>
  );
};

ContactsPage.propTypes = {
  organizationId: PropTypes.string.isRequired,
  initialModelFormItems: PropTypes.array,
  currentPage: PropTypes.number.isRequired,
  totalContacts: PropTypes.number,
  totalUnfilteredContacts: PropTypes.number,
  contacts: PropTypes.array.isRequired,
  setIsNewContact: PropTypes.func.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isSearchActive: PropTypes.bool,
  params: PropTypes.object,
};

export default ContactsPage;
